import React, { useState, useEffect, useRef, useContext } from 'react'
import { GlobalContext } from '../../../utils/globalState'
import EasyTaggingObject from './EasyTaggingObject'
import styles from './styles.module.css'

// component function
export default function EasyTagging(props) {

	const { chunks, video, origSegments, forceUpdate, setHasFinished, objRef1, objRef2, objRef3, objRef4, objRef5, objRef6, objRef7 } = props
	const [globalState, setGlobalState] = useContext(GlobalContext)	// eslint-disable-line no-unused-vars
	const [currentFrame, setCurrentFrame] = useState()	// eslint-disable-line no-unused-vars
	const [segmentIndex, setSegmentIndex] = useState()	// eslint-disable-line no-unused-vars
	const canvasRef = useRef()
	const ctxRef = useRef()
	const canvasWidthRef = useRef()
	const canvasHeightRef = useRef()
	const frameRef = useRef(0)
	const controlsContainerRef = useRef()
	const progressLineRef = useRef()
	const sceneIndexRef = useRef(0)
	const segmentIndexRef = useRef(0)
	const segmentsToTagRef = useRef(0)
	const segmentCountRef = useRef(0)
	const cueTypeCategory = globalState.cueTypes.find(cueType => cueType.id === getSegmentsInCurrentScene()[segmentIndexRef.current]?.cueType)?.cueTypeCategory
	const nameMaxChars = 28

	let spacePressed = false

	// calculate video canvas- & css sizes
	const processedWidth = video.width * video.processing_scale
	const processedHeight = video.height * video.processing_scale * (1/video.streamInfo.pixelAspectRatio)
	let canvasWidth = 640 * window.devicePixelRatio
	let canvasHeight = 360 * window.devicePixelRatio
	let canvasWidthCss = 1080
	let canvasHeightCss = canvasWidthCss/640*360
	if (processedHeight/processedWidth > 360/640) {
		canvasWidthCss = Math.ceil(processedWidth/processedHeight * canvasHeightCss)
	}
	if (processedWidth/processedHeight > 640/360) {
		canvasHeightCss = Math.ceil(processedHeight/processedWidth * canvasWidthCss)
	}
	canvasWidth = canvasWidthCss * window.devicePixelRatio
	canvasHeight = canvasHeightCss * window.devicePixelRatio

	const w = chunks[0].width/5
	const h = chunks[0].height/6

	// set refs (for js functions that don’t get state)
	canvasWidthRef.current = canvasWidth
	canvasHeightRef.current = canvasHeight

	// dynamic styles
	const canvasStyle = {
		maxWidth: canvasWidthCss
	}

	const arrowLeftStyle = {
		opacity: sceneIndexRef.current === 0 && segmentIndexRef.current === 0 ? .5 : 1,
		cursor: sceneIndexRef.current === 0 && segmentIndexRef.current === 0 ? 'default' : 'pointer',
		border: sceneIndexRef.current === 0 && segmentIndexRef.current === 0 ? '1px solid rgba(45,53,68,.3)' : '1px solid rgba(45,53,68,.15)'
	}

	const arrowRightStyle = {
		opacity: sceneIndexRef.current === video.scenes.length-1 && segmentIndexRef.current === getSegmentsInCurrentScene().length-1 ? .5 : 1,
		cursor: sceneIndexRef.current === video.scenes.length-1 && segmentIndexRef.current === getSegmentsInCurrentScene().length-1 ? 'default' : 'pointer',
		border: sceneIndexRef.current === video.scenes.length-1 && segmentIndexRef.current === getSegmentsInCurrentScene().length-1 ? '1px solid rgba(45,53,68,.3)' : '1px solid rgba(45,53,68,.15)'
	}

	const tagBtnStyle = {
		backgroundColor: cueTypeCategory === 1 ? 'var(--brand-color)' : cueTypeCategory === 2 ? 'var(--comms-color)' : 'var(--bg4-color)'
	}

	// context- & canvas refs setup on mount
	useEffect(() => {
		// canvas setup
		ctxRef.current = canvasRef.current.getContext('2d')
		ctxRef.current.imageSmoothingEnabled = true
	},[])

	// remove scenes with no segments with first segmentobject & go to first segment
	useEffect(() => {
		const newScenes = []
		video.scenes.map((scene, i) => {
			const currentSceneStartFrame = scene.frameNo
			const currentSceneEndFrame = i === video.scenes.length-1 ? video.frameCount-1 : video.scenes[i+1].frameNo-1
			const sceneObjects = video.objects.filter(o => o.frameNo >= currentSceneStartFrame && o.frameNo <= currentSceneEndFrame) // all objects in current scene
			const sceneSegmentIds = [...new Set(sceneObjects.map(obj => obj.segment))] // ids of segments in scene derived from objects
			const sceneSegments = video.segments.filter(s => sceneSegmentIds.indexOf(s.id) > -1) // all segments in current scene
			const minObjectsInSegment = video.analysisType === 3 ? 1 : 1 // minimum number of objects in a segment to avoid object scene creep - and if an object only exists in a few frames it may not be seen. Always keep still images (analysisType 3) to 1
			const sceneSegmentsWithMinObjects = sceneSegments.filter(s => sceneObjects.filter(o => o.segment === s.id).length >= minObjectsInSegment) // remove segments with less than x objects
			const sceneSegmentsWithFirstObject = sceneSegmentsWithMinObjects.filter(s => realStartFrame(s) === sceneObjects.filter(o => o.segment === s.id).sort((a,b) => a?.frameNo - b?.frameNo)[0].frameNo || s.creationMethod === 'GVI-logo') // remove scene segments that have already appeared in another scene, except segments detected by the logo API
			sceneSegmentsWithFirstObject.map(seg => seg.firstObjectFrame = sceneObjects.filter(o => o.segment === seg.id).sort((a,b) => a?.frameNo - b?.frameNo)[0].frameNo) // add frame number of first object in segment to sceneSegment
			sceneSegmentsWithFirstObject.sort((a,b) => a?.firstObjectFrame - b?.firstObjectFrame) // sort segments by first appearance in scene
			sceneSegmentsWithFirstObject.length > 0 && newScenes.push(scene)
			segmentsToTagRef.current += sceneSegmentsWithFirstObject.length // add to segment count
			return 0
		})
		video.scenes = newScenes
		segmentIndexRef.current = 1
		goPrevSegment()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	},[video])

	// keyboard listeners on origsegment update (to keep data on keyboard vs mouse click events in sync)
	useEffect(() => {
		// space bar video control
		window.addEventListener('keydown', keyPressed)
		window.addEventListener('keyup', keyReleased)
		// cleanup on unmount
		return () => {
			window.removeEventListener('keydown', keyPressed)
			window.removeEventListener('keyup', keyReleased)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	},[origSegments]) 

	// render video frame on all component updates
	useEffect(() => {
		renderFrame(frameRef.current)
 	})

	// handle key pressed events
	function keyPressed(e) {
		if (spacePressed) {
			e.preventDefault()
		} else if (e.keyCode === 32) { // spacebar
			e.preventDefault()
			spacePressed = true // flag to prevent repeated spaces
			toggleSegment()
		} else if (e.keyCode === 37) { // left arrow key
			segmentCountRef.current > 0 && segmentCountRef.current --
			goPrevSegment()
		} else if (e.keyCode === 39) { // right arrow key
			segmentCountRef.current < segmentsToTagRef.current-1 && segmentCountRef.current ++
			goNextSegment()
		}
	}

	// handle key released events
	function keyReleased(e) {
		if (e.keyCode === 32) { // space
			spacePressed = false
		}
	}

	// handle left/right arrow click
	function arrowClicked(dir) {
		if (dir === 'left') {
			segmentCountRef.current > 0 && segmentCountRef.current --
			goPrevSegment()
		} else {
			segmentCountRef.current < segmentsToTagRef.current-1 && segmentCountRef.current ++
			goNextSegment()
		}
	}

	// get segments in current scene
	function getSegmentsInCurrentScene() {
		if (!video.scenes.length || video.scenes[0]?.data?.dummy) { // if no scenes - create dummy-scene and return all segments (sorted by first object startframe)
			if (!video.scenes[0]?.data?.dummy) {
				video.scenes = [{frameNo:0, data:{dummy:true}}]
			}
			video.segments[0].firstObjectFrame === undefined && video.segments.map(seg => seg.firstObjectFrame = video.objects.filter(o => o.segment === seg.id).sort((a,b) => a?.frameNo - b?.frameNo)[0].frameNo)
			return video.segments.sort((a,b) => a?.firstObjectFrame - b?.firstObjectFrame) // sort segments by first appearance
		} else {
			const currentSceneStartFrame = video.scenes[sceneIndexRef.current].frameNo
			const currentSceneEndFrame = sceneIndexRef.current === video.scenes.length-1 ? video.frameCount-1 : video.scenes[sceneIndexRef.current+1].frameNo-1
			const sceneObjects = video.objects.filter(o => o.frameNo >= currentSceneStartFrame && o.frameNo <= currentSceneEndFrame) // all objects in current scene
			const sceneSegmentIds = [...new Set(sceneObjects.map(obj => obj.segment))] // ids of segments in scene derived from objects
			const sceneSegments = video.segments.filter(s => sceneSegmentIds.indexOf(s.id) > -1) // all segments in current scene
			const minObjectsInSegment = video.analysisType === 3 ? 1 : 1 // minimum number of objects in a segment to avoid object scene creep - and if an object only exists in a few frames it may not be seen. Always keep still images (analysisType 3) to 1
			const sceneSegmentsWithMinObjects = sceneSegments.filter(s => sceneObjects.filter(o => o.segment === s.id).length >= minObjectsInSegment) // remove segments with less than x objects
			const sceneSegmentsWithFirstObject = sceneSegmentsWithMinObjects.filter(s => realStartFrame(s) === sceneObjects.filter(o => o.segment === s.id).sort((a,b) => a?.frameNo - b?.frameNo)[0].frameNo || s.creationMethod === 'GVI-logo') // remove scene segments that have already appeared in another scene, except segments detected by the logo API
			sceneSegmentsWithFirstObject.map(seg => seg.firstObjectFrame = sceneObjects.filter(o => o.segment === seg.id).sort((a,b) => a?.frameNo - b?.frameNo)[0].frameNo) // add frame number of first object in segment to sceneSegment
			return sceneSegmentsWithFirstObject.sort((a,b) => a?.firstObjectFrame - b?.firstObjectFrame) // sort segments by first appearance in scene
		}
	}

	// get segment start frame based on first object frameNo since the value in segmentStartFrame doesn’t always match
	function realStartFrame(segment) {
		return video.objects.filter(o => o.segment === segment.id).sort((a,b) => a?.frameNo - b?.frameNo)[0].frameNo
	}

	// go to previous segment
	function goPrevSegment() {
		const currentSceneSegments = getSegmentsInCurrentScene()
		if (segmentIndexRef.current > 0) {
			segmentIndexRef.current --
			frameRef.current = currentSceneSegments[segmentIndexRef.current].firstObjectFrame
			setSegmentIndex(segmentIndexRef.current)
			setCurrentFrame(frameRef.current)
		} else if (sceneIndexRef.current > 0) { // previous scene
			sceneIndexRef.current --
			segmentIndexRef.current = getSegmentsInCurrentScene().length
			goPrevSegment()
		}
	}

	// go to next segment
	function goNextSegment() {
		const currentSceneSegments = getSegmentsInCurrentScene()
		if (segmentIndexRef.current < currentSceneSegments.length-1) {
			segmentIndexRef.current ++
			frameRef.current = currentSceneSegments[segmentIndexRef.current].firstObjectFrame
			setSegmentIndex(segmentIndexRef.current)
			setCurrentFrame(frameRef.current)
		} else if (sceneIndexRef.current < video.scenes.length-1) { // next scene
			sceneIndexRef.current ++
			segmentIndexRef.current = -1
			goNextSegment()
		}
	}

	// set selected cueType on segment and force parent update
	function toggleSegment() {
		const seg = getSegmentsInCurrentScene()[segmentIndexRef.current]
		if (seg === undefined) return;
		let origSeg = origSegments.find(s => s.id === seg.id)
		if (typeof origSeg !== 'object' || origSeg === null) {
			origSeg = seg
		}
		seg.cueType = seg.cueType !== null ? null : origSeg.cueType !== null ? origSeg.cueType : globalState.cueTypes.find(ct => ct.defaultForCreationMethod === seg.creationMethod).id // if not selected, select object and set cueType based on default for the segment’s creationMethod
		seg.dirty = seg.id < 0 ? false : seg.cueType === origSeg.cueType && seg.name.slice(0,nameMaxChars) === origSeg.name.slice(0,nameMaxChars) ? false : true // set 'dirty' on segment depending on diff from original value
		forceUpdate()
	}

	// cut frame image from chunk and paint it on canvas
	function renderFrame(rframe) {
		const ctx = ctxRef.current
		const chunk = Math.floor(rframe/30)
		const col = rframe % 5
		const row = Math.floor(rframe % 30 / 5)
		ctx.drawImage(chunks[chunk], w*col, h*row, w, h, 0, 0, canvasWidthRef.current, canvasHeightRef.current)

		// progress bar update
		const taggedPct = segmentCountRef.current / (segmentsToTagRef.current-1) * 100
		progressLineRef.current.style.width = taggedPct + '%'
		setHasFinished(taggedPct === 100)
	}

	const segment = getSegmentsInCurrentScene()[segmentIndexRef.current]
	const currentSceneStartFrame = video.scenes[sceneIndexRef.current].frameNo
	const segmentObjects = video.objects.filter(o => o.segment === segment?.id && o.frameNo >= currentSceneStartFrame)
	const objectsInFirstSegmentFrame = segmentObjects.filter(o => o.frameNo === segmentObjects[0].frameNo)
	const origSegment = origSegments.find(s => s.id === segment?.id)
	const tagButton = <div ref={objRef6} className={styles.button + ' ' + styles.tagBtn} style={tagBtnStyle} onClick={toggleSegment}>{cueTypeCategory ? 'Untag asset' : 'Tag as asset'}</div>

	// edit object
	const editObject = (objectsInFirstSegmentFrame.length > 0 &&
		<EasyTaggingObject
			objects={objectsInFirstSegmentFrame}
			segment={segment}
			origSegment={origSegment}
			forceUpdate={forceUpdate}
			toggleSegment={toggleSegment}
			nameMaxChars={nameMaxChars}
			objRef1={objRef1}
			objRef2={objRef2}
			objRef3={objRef3}
		/>
	)

	return (
		<>
			<div className={styles.videoWrapper}>
				<div className={styles.taggingContainer} style={canvasStyle}>
					<canvas id="canvas"
						className={styles.canvas}
						style={canvasStyle}
						ref={canvasRef}
						width={canvasWidth}
						height={canvasHeight}
					/>
					{editObject}
				</div>
			</div>
			<div ref={controlsContainerRef} className={styles.controlsContainer}>
				<div ref={objRef4} className={styles.lineContainer}>
					<div className={styles.line} />
				</div>
				<div ref={progressLineRef} className={styles.progressLine} />
			</div>
			<div className={styles.btnContainer}>
				<div ref={objRef5} className={styles.button + ' ' + styles.arrowLeft} style={arrowLeftStyle} onClick={e=>arrowClicked('left')}><span className={styles.arrowBtnLabel}>Prev. object</span></div>
				{tagButton}
				<div ref={objRef7} className={styles.button + ' ' + styles.arrowRight} style={arrowRightStyle} onClick={e=>arrowClicked('right')}><span className={styles.arrowBtnLabel}>{cueTypeCategory ? 'Next object' : '\u00A0Don’t tag'}</span></div>
			</div>
		</>
	)
}
